'use client';

import { Video } from 'lucide-react';
import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import {
  FloatingWindow,
  type FloatingWindowPosition,
  type FloatingWindowSize,
} from '@/components/dashboard/FloatingWindow';
import { LiveVideo } from '@/components/dashboard/LiveVideo';
import { DOCK_CAMERA_LABELS, dockCamUrl } from '@/lib/dock-camera';
import { CopVideoWindow } from './CopVideoWindow';
import type { CameraControlInputs } from './CameraControlOverlay';
import { cameraWindowKey, SINGLE_CAMERA, type CameraWindow } from './copReducers';

/**
 * Every camera picture floating over the COP map, drawn from one place.
 *
 * The COP page is the only surface that survives a selection change, so it is
 * the only correct owner of a window that must survive one. Before this, the
 * dock's Internal/External windows were state inside `DockSidebar` and died the
 * moment the operator clicked a drone or another sheet — no X pressed, picture
 * gone mid-approach — and the aircraft windows were wiped by the selection
 * reducer for the same reason in reverse.
 *
 * One list, one cascade. A dock window and an aircraft window used to open at
 * cascade index 0 each and land exactly on top of one another. The step each
 * window takes is the slot it was given at open (`freeCascadeSlot`), NOT its
 * position in this list — list positions are reused the moment a window closes,
 * while the windows that stay put keep the place their old position gave them.
 *
 * The only way out of a window is its own X, or — for the one unpinned aircraft
 * window the map click opens — the next click on another aircraft. Aircraft
 * windows also offer "return to the stage" when there is a stage to return to,
 * which is a placement verb and not a dismissal.
 */

export interface CopCameraWindowsProps {
  windows: CameraWindow[];
  /** Where each window was last dragged to, keyed by `cameraWindowKey`, so a
   *  window closed and reopened comes back where the operator put it rather
   *  than over the map they dragged it clear of. */
  positions: Record<string, FloatingWindowPosition>;
  /** The cascade step each open window holds, keyed by `cameraWindowKey`. Not
   *  the position in `windows`: see `freeCascadeSlot`. */
  cascadeSlots: Record<string, number>;
  /** What the operator resized each window to, keyed the same way. Lifted for
   *  the reason #645 lifted it out of `DockSidebar`: an operator who widened the
   *  picture to watch a tray approach must not get the opening size back the
   *  next time they open that camera. It moved up here with the rest of the
   *  window's ownership. */
  sizes: Record<string, FloatingWindowSize>;
  onSizeChange: (key: string, size: FloatingWindowSize) => void;
  onPositionChange: (key: string, position: FloatingWindowPosition) => void;
  onClose: (window: CameraWindow) => void;
  /** Bring a window to the front. Paint order is the order of `windows`, and a
   *  window resized to most of the viewport can bury another one whole - X and
   *  all - so a press has to be able to dig it back out. */
  onRaise: (window: CameraWindow) => void;
  /** Aircraft windows only: hand the picture back to the stage. */
  onDockDrone: (window: Extract<CameraWindow, { kind: 'drone' }>) => void;
  /** Aircraft windows only: flip whether this window survives the next click on
   *  an aircraft on the map. Dock windows never followed a selection, so they
   *  are never at risk from one and are given no pin. */
  onTogglePin: (window: Extract<CameraWindow, { kind: 'drone' }>) => void;
  /** Each aircraft picture's own verdict, filed against its stream id. The COP
   *  holds the map, because the details panel's health dot has to be readable
   *  from a tab that draws no picture. */
  onDroneStreamState: (streamId: string, state: HlsStreamState | null) => void;
  /** Resolves a dock id to its HLS stream path and the name to print. Returns
   *  null while the registry has not loaded the row, or after it is gone. */
  resolveDock: (dockId: string) => { topicPrefix: string; callsign: string } | null;
  /** Resolves an aircraft id to the name to print. Null when it is not in the
   *  picture the operator is filtered to. */
  resolveDroneCallsign: (droneId: string) => string | null;
  /** The camera pose THAT window's aircraft is reporting. Passed for every
   *  window, selected or not: withholding the command inputs from a background
   *  window is right, withholding the readout makes it print the product's
   *  dead-sensor token over a gimbal that is streaming fine. */
  resolveDronePose: (droneId: string) =>
    | Pick<CameraControlInputs, 'gimbalPitchDeg' | 'gimbalYawDeg' | 'zoomRatio' | 'cameraLens'>
    | undefined;
  /** The aircraft whose camera controls `cameraControl` was built from - the
   *  selected one, AND still drawable (in the current site picture). Only ITS
   *  windows may command a camera. */
  selectedDroneId: string | null;
  /** The aircraft the operator has selected, drawable or not. Differs from
   *  `selectedDroneId` only after a site filter change, which is the one case
   *  where "select this aircraft" is advice the operator has already taken. */
  pickedDroneId: string | null;
  /** May this operator's role command a camera at all. Only to decide WHICH
   *  refusal a background window prints: offering "select X to control" to a
   *  viewer is an instruction their role can never carry out. */
  canOperate: boolean;
  cameraControl?: CameraControlInputs;
  /** Passed through to the aircraft window so its dock verb can say whether the
   *  picture lands in the panel or back on the manual stage. */
  stageDrawsPicture: boolean;
  /** The selected aircraft's popped cameras, for that same decision. */
  selectedPoppedCameras: string[];
}

/**
 * Why a background window carries no camera controls, in words the operator can
 * act on.
 *
 * Three different facts, and only one of them is "select that aircraft":
 *  - a `viewer` is told nothing here, so the overlay's own role gate answers
 *    truthfully instead. Telling them to select an aircraft would offer a
 *    control their role can never reach, which is the same unactionable refusal
 *    this whole surface was fixed to stop printing.
 *  - an aircraft that IS selected but has left the site picture cannot be
 *    selected any harder. The site filter is what moved, so that is what is
 *    named.
 *  - otherwise the operator really can just pick it.
 */
function withheldReason(
  droneId: string,
  callsign: string,
  pickedDroneId: string | null,
  canOperate: boolean,
): string | null {
  if (!canOperate) return null;
  if (droneId === pickedDroneId) return `${callsign} IS NOT IN THE CURRENT SITE PICTURE`;
  return `SELECT ${callsign} TO CONTROL THIS CAMERA`;
}

export function CopCameraWindows({
  windows,
  positions,
  cascadeSlots,
  sizes,
  onSizeChange,
  onPositionChange,
  onClose,
  onRaise,
  onDockDrone,
  onTogglePin,
  onDroneStreamState,
  resolveDock,
  resolveDroneCallsign,
  resolveDronePose,
  selectedDroneId,
  pickedDroneId,
  canOperate,
  cameraControl,
  stageDrawsPicture,
  selectedPoppedCameras,
}: CopCameraWindowsProps) {
  return (
    <>
      {windows.map((win) => {
        const key = cameraWindowKey(win);
        const position = positions[key];
        const cascadeIndex = cascadeSlots[key] ?? 0;

        if (win.kind === 'drone') {
          // Live name first, the name at open time second, the id last. The
          // middle step is what keeps a window that is still playing from
          // retitling itself to a hex id when the aircraft leaves the
          // site-filtered live list.
          const callsign =
            resolveDroneCallsign(win.droneId)
            ?? win.callsign
            ?? win.droneId.slice(-6).toUpperCase();
          // Is this window's aircraft the one the rest of the COP is about?
          //
          // Two things hang off it. Camera control: `cameraControl` is assembled
          // from the SELECTED drone's command route, companion heartbeat and
          // pending phases, so handing it to a window pointed at a different
          // airframe would slew the selected aircraft's gimbal from a picture of
          // another one. Every field of CameraControlInputs is optional and the
          // overlay withholds a control it has no inputs for, so a background
          // window degrades to picture-only.
          //
          // And the dock verb: the details panel shows one aircraft, so there is
          // no inline home for a background window's picture. Withheld rather
          // than offered-and-discarding.
          const isSelected = win.droneId === selectedDroneId;
          return (
            <CopVideoWindow
              key={key}
              droneId={win.droneId}
              cameraId={win.cameraId === SINGLE_CAMERA ? undefined : win.cameraId}
              cascadeIndex={cascadeIndex}
              position={position}
              onPositionChange={(next) => onPositionChange(key, next)}
              callsign={callsign}
              // Named, because up to four of these can be open at once and X is
              // the only way out of any of them. "Close video window" four times
              // over leaves a screen-reader or voice-control operator no way to
              // say which one they mean.
              closeLabel={`Close ${callsign} video window`}
              onDock={isSelected ? () => onDockDrone(win) : undefined}
              onClose={() => onClose(win)}
              onRaise={() => onRaise(win)}
              // The pin is offered on EVERY aircraft window, selected or not.
              // Unlike camera control and the dock verb it says nothing about
              // the aircraft — it is a fact about this window's own survival,
              // and a window the operator has navigated away from is exactly
              // the one they most need to be able to keep.
              onStreamState={onDroneStreamState}
              // Only a window the map click opened by itself may remove itself
              // when this aircraft turns out to have no camera at all.
              transient={!win.pinned}
              pinned={win.pinned}
              onTogglePin={() => onTogglePin(win)}
              // ONE name whatever the state — `aria-pressed` on the control
              // carries pressed/not. Named per aircraft because four of these
              // can be on screen at once.
              pinLabel={`Pin ${callsign} video window`}
              // Both only describe where the SELECTED aircraft's picture would
              // land, so they are false for any other window.
              stageDrawsPicture={isSelected && stageDrawsPicture}
              poppedCameras={isSelected ? selectedPoppedCameras : []}
              // The POSE first, for this window's own aircraft, so a background
              // window reads out what its gimbal is actually reporting. The
              // control inputs then overwrite it for the selected aircraft,
              // which is the same data plus everything that can command.
              {...resolveDronePose(win.droneId)}
              {...(isSelected ? cameraControl : undefined)}
              // Spread AFTER the control inputs, so a background window can
              // never be handed one. Without it the overlay falls through to
              // its `canOperate = false` default and prints "ROLE CANNOT
              // COMMAND — VIEW ONLY" over the picture: a false claim about the
              // operator's permissions, in the exact words the product uses for
              // a real `viewer`. It also names the one action that brings the
              // controls back, which is the same action that brings back the
              // dock verb withheld above.
              controlWithheldReason={
                isSelected ? null : withheldReason(win.droneId, callsign, pickedDroneId, canOperate)
              }
            />
          );
        }

        const dock = resolveDock(win.dockId);
        const label = DOCK_CAMERA_LABELS[win.cam];
        const callsign =
          dock?.callsign ?? win.callsign ?? win.dockId.slice(-6).toUpperCase();
        return (
          <FloatingWindow
            key={key}
            cascadeIndex={cascadeIndex}
            position={position}
            onPositionChange={(next) => onPositionChange(key, next)}
            size={sizes[key]}
            onSizeChange={(next) => onSizeChange(key, next)}
            icon={<Video className="size-4 text-status-cyan-fg" aria-hidden />}
            title={`${label} Camera · ${callsign}`}
            onClose={() => onClose(win)}
            onRaise={() => onRaise(win)}
            // Named, for the same reason the aircraft windows are: two docks'
            // Internal cameras can be open side by side, and both would
            // otherwise announce "Close internal dock camera".
            closeLabel={`Close ${label.toLowerCase()} camera on ${callsign}`}
            // Named per dock as well as per slot: two docks' Internal cameras
            // open side by side would otherwise give a screen reader two
            // identical grips, which is what this prop exists to prevent.
            resizeLabel={`Resize ${label.toLowerCase()} camera on ${callsign}`}
          >
            {/* `h-full`, not `aspect-video`: the window is resizable since #645,
                so the body follows the window rather than dictating to it. */}
            <div className="flex h-full w-full items-center justify-center bg-black">
              {/* A window is never closed out from under the operator, not even
                  when its dock cannot be resolved — that is the whole point of
                  this model. It says so instead, and plays nothing: an empty
                  topic prefix makes a URL the player would retry for ever.

                  The realistic way to land here is the COP's one `/api/docks`
                  fetch having failed (it is a mount-time fetch with no retry,
                  and its failure is only a console warning), so the sentence
                  names the REGISTRY rather than blaming the dock's own
                  configuration - `topic_prefix` is NOT NULL and server-derived,
                  so "this dock has no stream path" would be a claim about the
                  hardware that is almost never the truth. */}
              {dock === null ? (
                <p className="px-4 text-center text-[11px] uppercase tracking-wider text-on-media-muted-fg">
                  Dock registry unavailable — cannot resolve this dock&apos;s stream
                </p>
              ) : (
                <LiveVideo
                  url={dockCamUrl(dock.topicPrefix, win.cam)}
                  label={`${label} dock camera for ${callsign}`}
                  controls={false}
                  // `object-contain`, not `cover`: the window resizes freely, and
                  // `cover` crops at every aspect but the source's - so an
                  // operator who widened the window to see more would see less.
                  className="h-full w-full object-contain"
                />
              )}
            </div>
          </FloatingWindow>
        );
      })}
    </>
  );
}
